import gql from 'graphql-tag';
import { DateTime } from "luxon";
import className from 'classnames/bind';
import { HoursManualFragment } from '@graphqlTypes';
import { Container, RevealWrapper, SectionHeader } from '@components';
import { isMonthInRange, monthsUntilRange } from '@/utils/monthRange';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './HoursManual.module.scss';
import { useMemo, useRef } from 'react';
const cx = className.bind(styles);

export default function HoursManual(props: HoursManualFragment) {
  const { headline, subhead, monthRanges, specialDates } = props;
  const showHeader = Boolean(headline || subhead);

  const currentMonth = DateTime.now().setZone('America/New_York').month;

  const currentMonthRange = useMemo(() => {
    return monthRanges.find(range => {
      if (!range.startMonth || !range.endMonth) return false;
      return isMonthInRange(range.startMonth, range.endMonth, currentMonth);
    });
  }, [monthRanges, currentMonth]);

  const otherDates = useMemo(() => {
    return monthRanges
      .filter(range => range !== currentMonthRange)
      .sort((a, b) => monthsUntilRange(a.startMonth || '', currentMonth) - monthsUntilRange(b.startMonth || '', currentMonth));
  }, [monthRanges, currentMonthRange, currentMonth]);

  const { isDesktop } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  return (
    <section className={cx(["hours-manual"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          {showHeader && (
            <SectionHeader
              headline={headline}
              subhead={subhead}
              isRevealEnabled={true}
              isInView={isInView}
            />
          )}
          <div className={cx("grid")}>
            <div className={cx("current-month")}>
              <h2 className={cx("title", "title-lc-l")} aria-label={currentMonthRange?.label}>{currentMonthRange?.label}</h2>
              <div className={cx("hours-list")}>
                {currentMonthRange?.dayRows.map((row, index) => (
                  <p className={cx('hours-list__hours', 'text-m')} key={index}>
                    <span className={cx('text')} aria-label={row?.textLeft}>{row?.textLeft}</span>
                    {row.textRight && (
                      <>
                        <span className={cx("divider")} />
                        <span className={cx('text')} aria-label={row?.textRight}>{row?.textRight}</span>
                      </>
                    )}
                  </p>
                ))}
              </div>
            </div>
            <div className={cx("other-dates")}>
              {otherDates?.map((range, index) => (
                <div className={cx("other-date")} key={index}>
                  <h3 className={cx("title", "title-lc-s")} aria-label={range?.label}>{range?.label}</h3>
                  <div className={cx("hours-list")}>
                    {range.dayRows.map((row, dayIndex) => (
                      <p className={cx('hours-list__hours', 'text-m')} key={dayIndex}>
                        <span className={cx('text')} aria-label={row?.textLeft}>{row?.textLeft}</span>
                        {row.textRight && (
                          <>
                            <span className={cx("divider")} />
                            <span className={cx('text')} aria-label={row?.textRight}>{row?.textRight}</span>
                          </>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
              {specialDates?.dayRows?.length && (
                <div className={cx("other-date")}>
                  <h3 className={cx("title", "title-lc-s")} aria-label={specialDates?.label}>{specialDates?.label}</h3>
                  <div className={cx("hours-list")}>
                    {specialDates?.dayRows.map((row, dayIndex) => (
                      <p className={cx('hours-list__hours', 'text-m')} key={dayIndex}>
                        <span className={cx('text')} aria-label={row?.textLeft}>{row?.textLeft}</span>
                        {row.textRight && (
                          <>
                            <span className={cx("divider")} />
                            <span className={cx('text')} aria-label={row?.textRight}>{row?.textRight}</span>
                          </>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </RevealWrapper>
    </section>
  )
}

HoursManual.fragments = {
  entry: gql`
    fragment HoursManual on FlexiblePageContentContentHoursManualLayout {
      headline
      subhead
      monthRanges {
        label
        startMonth
        endMonth
        dayRows {
          textLeft
          textRight
        }
      }
      specialDates {
        label 
        dayRows {
          textLeft
          textRight
        }
      }
    }
  `,
};